/**
 * DocumentUploadModal
 *
 * Shared modal for adding / editing documents in two modes:
 *   - "deal"   → adds/edits a deal document (crm_x_deal_documents)
 *   - "entity" → adds/edits an entity document (crm_x_entity_documents)
 *
 * Deal mode also supports linking an existing entity document from the
 * entity's library.  When a library doc is selected the form auto-fills
 * from it and the resulting deal document gets collectionStatus="linked".
 *
 * AC001: Create deal document
 * AC002: Create entity document
 * AC003: Link entity document to deal
 * AC004: Edit mode pre-fills all fields
 * AC005: Document type dropdown grouped by category
 */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Link2 } from "lucide-react";

import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentType {
  id: string;
  name: string;
  categoryName: string | null;
}

interface EntityDocumentSummary {
  id: string;
  documentTypeName: string;
  fileName: string;
  fileUrl: string | null;
  documentDate: string | null;
  uploadedAt: string;
  documentTypeId: string;
}

export interface DocumentUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "deal" | "entity";
  // Deal mode
  dealId?: string;
  accountId?: string;
  contactId?: string;
  applicantRole?: string;
  // Entity mode
  entityId?: string;
  entityType?: "account" | "contact";
  // Edit mode
  existingDocument?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

const dealDocFormSchema = z.object({
  documentTypeId: z.string().min(1, "Document type is required"),
  fileName: z.string().optional().nullable(),
  fileUrl: z.string().optional().nullable(),
  documentDate: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  linkedEntityDocumentId: z.string().optional().nullable(),
});

const entityDocFormSchema = z.object({
  documentTypeId: z.string().min(1, "Document type is required"),
  fileName: z.string().min(1, "File name is required"),
  fileUrl: z.string().optional().nullable(),
  documentDate: z.string().optional().nullable(),
  validFrom: z.string().optional().nullable(),
  validUntil: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

type DealDocFormData = z.infer<typeof dealDocFormSchema>;
type EntityDocFormData = z.infer<typeof entityDocFormSchema>;

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

const dealDocDefaults: DealDocFormData = {
  documentTypeId: "",
  fileName: "",
  fileUrl: "",
  documentDate: "",
  notes: "",
  linkedEntityDocumentId: null,
};

const entityDocDefaults: EntityDocFormData = {
  documentTypeId: "",
  fileName: "",
  fileUrl: "",
  documentDate: "",
  validFrom: "",
  validUntil: "",
  notes: "",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Group document types by category for the Select dropdown */
function groupByCategory(types: DocumentType[]): Map<string, DocumentType[]> {
  const map = new Map<string, DocumentType[]>();
  for (const dt of types) {
    const cat = dt.categoryName ?? "Other";
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(dt);
  }
  return map;
}

/** Coerce empty string → null for optional fields before submission */
function emptyToNull(val: string | null | undefined): string | null {
  if (!val || val.trim() === "") return null;
  return val;
}

/** Format ISO date string to YYYY-MM-DD for date inputs */
function toDateInputValue(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    return dateStr.substring(0, 10);
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentUploadModal({
  open,
  onOpenChange,
  mode,
  dealId,
  accountId,
  contactId,
  applicantRole,
  entityId,
  entityType,
  existingDocument,
}: DocumentUploadModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEdit = !!existingDocument;

  // Track which library doc the user has selected (deal mode only)
  const [selectedLibraryDocId, setSelectedLibraryDocId] = useState<string>("__none__");

  // ---------------------------------------------------------------------------
  // Forms
  // ---------------------------------------------------------------------------

  const dealForm = useForm<DealDocFormData>({
    resolver: zodResolver(dealDocFormSchema),
    defaultValues: dealDocDefaults,
    mode: "onChange",
  });

  const entityForm = useForm<EntityDocFormData>({
    resolver: zodResolver(entityDocFormSchema),
    defaultValues: entityDocDefaults,
    mode: "onChange",
  });

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  const { data: documentTypes = [] } = useQuery<DocumentType[]>({
    queryKey: ["/api/crm/document-types"],
    enabled: open,
  });

  // Fetch entity library docs for linking (deal mode only, when accountId or contactId known)
  const entityLibraryEndpoint =
    accountId
      ? `/api/crm/accounts/${accountId}/documents`
      : contactId
        ? `/api/crm/contacts/${contactId}/documents`
        : null;

  const { data: entityLibraryDocs = [] } = useQuery<EntityDocumentSummary[]>({
    queryKey: [entityLibraryEndpoint],
    enabled: open && mode === "deal" && !!entityLibraryEndpoint,
  });

  // ---------------------------------------------------------------------------
  // Reset on open / close
  // ---------------------------------------------------------------------------

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      dealForm.reset(dealDocDefaults);
      entityForm.reset(entityDocDefaults);
      setSelectedLibraryDocId("__none__");
    }
    onOpenChange(newOpen);
  };

  useEffect(() => {
    if (!open) return;
    setSelectedLibraryDocId("__none__");

    if (mode === "deal") {
      if (isEdit && existingDocument) {
        dealForm.reset({
          documentTypeId: (existingDocument.documentTypeId as string) ?? "",
          fileName: (existingDocument.fileName as string) ?? "",
          fileUrl: (existingDocument.fileUrl as string) ?? "",
          documentDate: toDateInputValue(existingDocument.documentDate as string | null),
          notes: (existingDocument.notes as string) ?? "",
          linkedEntityDocumentId: (existingDocument.linkedEntityDocumentId as string) ?? null,
        });
        if (existingDocument.linkedEntityDocumentId) {
          setSelectedLibraryDocId(existingDocument.linkedEntityDocumentId as string);
        }
      } else {
        dealForm.reset(dealDocDefaults);
      }
    } else {
      if (isEdit && existingDocument) {
        entityForm.reset({
          documentTypeId: (existingDocument.documentTypeId as string) ?? "",
          fileName: (existingDocument.fileName as string) ?? "",
          fileUrl: (existingDocument.fileUrl as string) ?? "",
          documentDate: toDateInputValue(existingDocument.documentDate as string | null),
          validFrom: toDateInputValue(existingDocument.validFrom as string | null),
          validUntil: toDateInputValue(existingDocument.validUntil as string | null),
          notes: (existingDocument.notes as string) ?? "",
        });
      } else {
        entityForm.reset(entityDocDefaults);
      }
    }
  }, [open, mode, isEdit, existingDocument]);  

  // ---------------------------------------------------------------------------
  // Library doc auto-fill (deal mode)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (mode !== "deal" || selectedLibraryDocId === "__none__") return;
    const libDoc = entityLibraryDocs.find((d) => d.id === selectedLibraryDocId);
    if (!libDoc) return;
    dealForm.setValue("documentTypeId", libDoc.documentTypeId, { shouldValidate: true });
    dealForm.setValue("fileName", libDoc.fileName ?? "", { shouldValidate: false });
    dealForm.setValue("fileUrl", libDoc.fileUrl ?? "", { shouldValidate: false });
    dealForm.setValue("documentDate", toDateInputValue(libDoc.documentDate), { shouldValidate: false });
    dealForm.setValue("linkedEntityDocumentId", libDoc.id, { shouldValidate: false });
  }, [selectedLibraryDocId, entityLibraryDocs, mode]);  

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const dealDocMutation = useMutation({
    mutationFn: async (data: DealDocFormData) => {
      const isLinked = selectedLibraryDocId !== "__none__" && selectedLibraryDocId !== "";
      const payload = {
        documentTypeId: emptyToNull(data.documentTypeId),
        fileName: emptyToNull(data.fileName),
        fileUrl: emptyToNull(data.fileUrl),
        documentDate: emptyToNull(data.documentDate),
        notes: emptyToNull(data.notes),
        applicantRole: applicantRole ?? "borrower",
        accountId: accountId ?? null,
        contactId: contactId ?? null,
        ...(isLinked
          ? {
              collectionStatus: "linked",
              linkedEntityDocumentId: selectedLibraryDocId,
            }
          : {
              linkedEntityDocumentId: null,
            }),
      };

      if (isEdit && existingDocument?.id) {
        return apiRequest("PATCH", `/api/crm/deal-documents/${existingDocument.id}`, payload);
      }
      return apiRequest("POST", `/api/crm/deals/${dealId}/documents`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", dealId, "documents"] });
      toast({
        title: isEdit ? "Document updated" : "Document added successfully",
        description: isEdit
          ? "Document details have been updated."
          : "The document has been added to the checklist.",
      });
      handleOpenChange(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Could not save document. Please try again.",
        variant: "destructive",
      });
    },
  });

  const entityDocMutation = useMutation({
    mutationFn: async (data: EntityDocFormData) => {
      const payload = {
        documentTypeId: data.documentTypeId,
        fileName: data.fileName,
        fileUrl: emptyToNull(data.fileUrl),
        documentDate: emptyToNull(data.documentDate),
        validFrom: emptyToNull(data.validFrom),
        validUntil: emptyToNull(data.validUntil),
        notes: emptyToNull(data.notes),
        ...(entityType === "account"
          ? { accountId: entityId }
          : { contactId: entityId }),
      };

      if (isEdit && existingDocument?.id) {
        return apiRequest("PATCH", `/api/crm/entity-documents/${existingDocument.id}`, payload);
      }
      return apiRequest("POST", `/api/crm/entity-documents`, payload);
    },
    onSuccess: () => {
      if (entityLibraryEndpoint) {
        queryClient.invalidateQueries({ queryKey: [entityLibraryEndpoint] });
      }
      toast({
        title: isEdit ? "Document updated" : "Document uploaded successfully",
        description: isEdit
          ? "Document details have been updated."
          : "The document has been added to the library.",
      });
      handleOpenChange(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Could not save document. Please try again.",
        variant: "destructive",
      });
    },
  });

  const isPending = dealDocMutation.isPending || entityDocMutation.isPending;

  // ---------------------------------------------------------------------------
  // Submit handlers
  // ---------------------------------------------------------------------------

  function onDealSubmit(data: DealDocFormData) {
    dealDocMutation.mutate(data);
  }

  function onEntitySubmit(data: EntityDocFormData) {
    entityDocMutation.mutate(data);
  }

  // ---------------------------------------------------------------------------
  // Grouped document types for Select
  // ---------------------------------------------------------------------------

  const typeGroups = groupByCategory(documentTypes);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const title =
    mode === "deal"
      ? isEdit
        ? "Edit Document"
        : "Add Document to Checklist"
      : isEdit
        ? "Edit Document"
        : "Upload Document";

  const submitLabel = isEdit ? "Update" : mode === "deal" ? "Save" : "Upload";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="document-upload-modal"
      >
        <DialogHeader>
          <DialogTitle data-testid="modal-title">{title}</DialogTitle>
          {applicantRole && mode === "deal" && (
            <DialogDescription data-testid="modal-description">
              Role:{" "}
              <Badge variant="outline" className="text-xs uppercase font-semibold ml-1">
                {applicantRole}
              </Badge>
            </DialogDescription>
          )}
        </DialogHeader>

        {/* ---------------------------------------------------------------- */}
        {/* DEAL MODE                                                          */}
        {/* ---------------------------------------------------------------- */}
        {mode === "deal" && (
          <Form {...dealForm}>
            <form
              onSubmit={dealForm.handleSubmit(onDealSubmit)}
              className="space-y-4"
              data-testid="deal-document-form"
            >
              {/* Document Type */}
              <FormField
                control={dealForm.control}
                name="documentTypeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Type *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ""}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-document-type">
                          <SelectValue placeholder="Select document type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent data-testid="document-type-options">
                        {Array.from(typeGroups.entries()).map(([cat, types]) => (
                          <div key={cat}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              {cat}
                            </div>
                            {types.map((dt) => (
                              <SelectItem
                                key={dt.id}
                                value={dt.id}
                                data-testid={`doc-type-option-${dt.id}`}
                              >
                                {dt.name}
                              </SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* File Name */}
              <FormField
                control={dealForm.control}
                name="fileName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>File Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Tax Return FY2024.pdf"
                        data-testid="input-file-name"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* File URL */}
              <FormField
                control={dealForm.control}
                name="fileUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>File URL <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://..."
                        data-testid="input-file-url"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Document Date */}
              <FormField
                control={dealForm.control}
                name="documentDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        data-testid="input-document-date"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Entity Library Linking (only when entity docs available) */}
              {entityLibraryDocs.length > 0 && (
                <div
                  className="border rounded-md p-3 space-y-2 bg-muted/30"
                  data-testid="entity-library-section"
                >
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                    OR Link from Entity Library
                  </div>
                  <RadioGroup
                    value={selectedLibraryDocId}
                    onValueChange={setSelectedLibraryDocId}
                    data-testid="library-doc-radio-group"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value="__none__"
                        id="lib-doc-none"
                        data-testid="library-doc-none"
                      />
                      <Label
                        htmlFor="lib-doc-none"
                        className="text-sm text-muted-foreground cursor-pointer"
                      >
                        No link — enter details manually
                      </Label>
                    </div>
                    {entityLibraryDocs.map((libDoc) => (
                      <div
                        key={libDoc.id}
                        className="flex items-start gap-2"
                        data-testid={`library-doc-option-${libDoc.id}`}
                      >
                        <RadioGroupItem
                          value={libDoc.id}
                          id={`lib-doc-${libDoc.id}`}
                          className="mt-0.5"
                        />
                        <Label
                          htmlFor={`lib-doc-${libDoc.id}`}
                          className="text-sm leading-snug cursor-pointer"
                        >
                          <span className="font-medium">{libDoc.documentTypeName}</span>
                          {libDoc.fileName && (
                            <span className="text-muted-foreground ml-1">— {libDoc.fileName}</span>
                          )}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}

              {/* Notes */}
              <FormField
                control={dealForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any additional notes..."
                        className="min-h-[80px] resize-none"
                        data-testid="textarea-notes"
                        rows={3}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  data-testid="cancel-button"
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isPending}
                  data-testid="submit-button"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {submitLabel}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* ENTITY MODE                                                        */}
        {/* ---------------------------------------------------------------- */}
        {mode === "entity" && (
          <Form {...entityForm}>
            <form
              onSubmit={entityForm.handleSubmit(onEntitySubmit)}
              className="space-y-4"
              data-testid="entity-document-form"
            >
              {/* Document Type */}
              <FormField
                control={entityForm.control}
                name="documentTypeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Type *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ""}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-document-type">
                          <SelectValue placeholder="Select document type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent data-testid="document-type-options">
                        {Array.from(typeGroups.entries()).map(([cat, types]) => (
                          <div key={cat}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              {cat}
                            </div>
                            {types.map((dt) => (
                              <SelectItem
                                key={dt.id}
                                value={dt.id}
                                data-testid={`doc-type-option-${dt.id}`}
                              >
                                {dt.name}
                              </SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* File Name */}
              <FormField
                control={entityForm.control}
                name="fileName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>File Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. Tax Return FY2024.pdf"
                        data-testid="input-file-name"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* File URL */}
              <FormField
                control={entityForm.control}
                name="fileUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>File URL <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://..."
                        data-testid="input-file-url"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Document Date */}
              <FormField
                control={entityForm.control}
                name="documentDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        data-testid="input-document-date"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Valid From */}
              <FormField
                control={entityForm.control}
                name="validFrom"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valid From <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        data-testid="input-valid-from"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Valid Until */}
              <FormField
                control={entityForm.control}
                name="validUntil"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valid Until <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        data-testid="input-valid-until"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Notes */}
              <FormField
                control={entityForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Any additional notes..."
                        className="min-h-[80px] resize-none"
                        data-testid="textarea-notes"
                        rows={3}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  data-testid="cancel-button"
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isPending}
                  data-testid="submit-button"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {submitLabel}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
